import * as React from 'react'
import { Loader2 } from 'lucide-react'
import type { BillingSummaryDto } from '@lib/dto/billing.dto'
import { BILLING } from '@lib/constants/billing'
import { cardBrandLabel } from '@lib/helpers/card-brand'
import { formatCents, microsToWholeCents, parseDollarsToCents } from '@lib/helpers/money'
import { autoReloadOnSchema, type AutoReloadOn } from '@lib/input/billing.input'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, fieldMessageId } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toast'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useBillingActions } from '@/hooks/useBilling'

type FieldName = 'trigger' | 'amount' | 'cap'

/** Dollars as typed, from cents: "10.00". */
function dollars(cents: number | null, fallback: number): string {
  return ((cents ?? fallback) / 100).toFixed(2)
}

/** The settings in the form, or what's wrong with each field. */
function readSettings(values: Record<FieldName, string>): { settings: AutoReloadOn } | { errors: Partial<Record<FieldName, string>> } {
  const trigger = parseDollarsToCents(values.trigger)
  const amount = parseDollarsToCents(values.amount)
  const cap = parseDollarsToCents(values.cap)
  const errors: Partial<Record<FieldName, string>> = {}
  if (trigger === null) errors.trigger = 'Enter an amount like 10 or 10.00.'
  if (amount === null) errors.amount = 'Enter an amount like 50 or 50.00.'
  if (cap === null) errors.cap = 'Enter an amount like 200 or 200.00.'
  const parsed = autoReloadOnSchema.safeParse({ enabled: true, triggerCents: trigger ?? Number.NaN, amountCents: amount ?? Number.NaN, monthlyCapCents: cap ?? Number.NaN })
  if (parsed.success && Object.keys(errors).length === 0) return { settings: parsed.data }
  for (const issue of parsed.success ? [] : parsed.error.issues) {
    const field = issue.path[0]
    if (field === 'triggerCents' && !errors.trigger) errors.trigger = `Between $0.01 and ${formatCents(BILLING.thresholdMaxCents)}.`
    if (field === 'amountCents' && !errors.amount) errors.amount = `Between ${formatCents(BILLING.purchaseMinCents)} and ${formatCents(BILLING.purchaseMaxCents)}.`
    if (field === 'monthlyCapCents' && !errors.cap) {
      errors.cap = issue.code === 'custom' ? 'At least the amount it adds.' : `Between ${formatCents(BILLING.purchaseMinCents)} and ${formatCents(BILLING.autoReloadMonthlyCapMaxCents)}.`
    }
  }
  return { errors }
}

interface AutoReloadFormProps {
  summary: BillingSummaryDto
  onDone: () => void
}

/**
 * Mounted each time the dialog opens, starting from the current settings (or
 * suggestions). With no saved card, or a paused auto-reload, the primary
 * action saves a card on Stripe's page and turns the settings on with it.
 */
function AutoReloadForm({ summary, onDone }: AutoReloadFormProps) {
  const { setAutoReload, createCardSetup } = useBillingActions()
  const reload = summary.autoReload
  const [values, setValues] = React.useState<Record<FieldName, string>>(() => ({
    trigger: dollars(reload.triggerMicros === null ? null : microsToWholeCents(reload.triggerMicros), 1_000),
    amount: dollars(reload.amountCents, 5_000),
    cap: dollars(reload.monthlyCapCents, 20_000),
  }))
  const [errors, setErrors] = React.useState<Partial<Record<FieldName, string>>>({})
  const [formError, setFormError] = React.useState<string | null>(null)
  const [leaving, setLeaving] = React.useState(false)
  const needsCard = summary.card === null || reload.status === 'paused'
  const busy = setAutoReload.isPending || createCardSetup.isPending || leaving

  function setValue(field: FieldName, value: string): void {
    setValues((current) => ({ ...current, [field]: value }))
  }

  /** Sends the Owner to Stripe's page to save a card, with these settings to turn on once it's saved (or none). */
  async function saveCard(settings: AutoReloadOn | null): Promise<void> {
    const setup = await createCardSetup.mutateAsync(settings ? { autoReload: settings } : {})
    if (!setup.checkoutUrl) throw new Error('Stripe didn’t open a page to save the card. Try again.')
    setLeaving(true)
    window.location.assign(setup.checkoutUrl)
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    const read = readSettings(values)
    if ('errors' in read) {
      setErrors(read.errors)
      return
    }
    setErrors({})
    setFormError(null)
    try {
      if (needsCard) {
        await saveCard(read.settings)
        return
      }
      await setAutoReload.mutateAsync(read.settings)
      toast.success(reload.status === 'active' ? 'Auto-reload saved' : 'Auto-reload is on')
      onDone()
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not save auto-reload.'))
    }
  }

  async function turnOff(): Promise<void> {
    setFormError(null)
    try {
      await setAutoReload.mutateAsync({ enabled: false })
      toast.success('Auto-reload is off')
      onDone()
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not turn auto-reload off.'))
    }
  }

  async function differentCard(): Promise<void> {
    setFormError(null)
    try {
      await saveCard(null)
    } catch (failure) {
      setFormError(apiErrorMessage(failure, 'Could not open the card page.'))
    }
  }

  function primaryLabel(): string {
    if (needsCard) return reload.status === 'paused' ? 'Save a card and resume' : 'Save card and turn on'
    return reload.status === 'active' ? 'Save' : 'Turn on'
  }

  const fields: { name: FieldName; label: string; hint: string }[] = [
    { name: 'trigger', label: 'When the balance falls below', hint: 'Checked after each live envelope.' },
    { name: 'amount', label: 'Add', hint: `From ${formatCents(BILLING.purchaseMinCents)} to ${formatCents(BILLING.purchaseMaxCents)}, plus sales tax where it applies.` },
    { name: 'cap', label: 'Monthly cap', hint: 'The most auto-reload adds in a calendar month. It stops there until the next month.' },
  ]

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <DialogHeader>
        <DialogTitle>Auto-reload</DialogTitle>
        <DialogDescription>
          {needsCard
            ? 'You’ll save a card on Stripe’s page (nothing is charged now), then come back here with auto-reload on.'
            : `PixelSign charges ${summary.card ? `your ${cardBrandLabel(summary.card.brand)} ending ${summary.card.last4}` : 'your saved card'} when a live envelope leaves the balance below the amount you set.`}
        </DialogDescription>
      </DialogHeader>
      {fields.map((field) => (
        <Field key={field.name} label={field.label} htmlFor={`auto-reload-${field.name}`} error={errors[field.name] ?? null} hint={field.hint}>
          <Input
            id={`auto-reload-${field.name}`}
            inputMode="decimal"
            value={values[field.name]}
            aria-invalid={errors[field.name] ? true : undefined}
            aria-describedby={fieldMessageId(`auto-reload-${field.name}`)}
            onChange={(event) => setValue(field.name, event.target.value)}
          />
        </Field>
      ))}
      {formError ? <p className="text-body text-danger">{formError}</p> : null}
      <DialogFooter className="flex-wrap">
        {reload.status === 'active' ? (
          <Button type="button" variant="destructive-quiet" disabled={busy} onClick={() => void turnOff()} className="sm:mr-auto">
            Turn off
          </Button>
        ) : null}
        {!needsCard ? (
          <Button type="button" variant="ghost" disabled={busy} onClick={() => void differentCard()}>
            Use a different card
          </Button>
        ) : null}
        <Button type="button" variant="secondary" onClick={onDone}>
          Cancel
        </Button>
        <Button type="submit" disabled={busy}>
          {busy ? <Loader2 className="animate-spin" /> : null}
          {primaryLabel()}
        </Button>
      </DialogFooter>
    </form>
  )
}

interface AutoReloadDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  summary: BillingSummaryDto
}

/** An Owner sets up, changes, or turns off auto-reload, saving a card on Stripe's page when it needs one. */
export function AutoReloadDialog({ open, onOpenChange, summary }: AutoReloadDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <AutoReloadForm summary={summary} onDone={() => onOpenChange(false)} />
      </DialogContent>
    </Dialog>
  )
}
